import React, { useState } from 'react';
import { Upload, Button, message, Row, Col, Typography } from 'antd';
import { UploadOutlined, FileExcelOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { prescriptionService } from '../../services';
import { getApiErrorMessage } from '../../utils/error';
import './ImportPrescriptionPage.css';

const { Text, Link } = Typography;

const ImportPrescriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [downloading, setDownloading] = useState(false);

  const importMutation = useMutation({
    mutationFn: (file: File) => prescriptionService.importExcel(file),
    onSuccess: (data) => {
      message.success(`Import thành công ${data.data?.imported || 0} toa thuốc!`);
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      setFileList([]);
      navigate('/facility/prescriptions');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Import thất bại!');
    },
  });

  const handleUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(-1));
  };

  const handleImport = () => {
    if (fileList.length === 0) {
      message.warning('Vui lòng chọn file Excel để import!');
      return;
    }

    const file = fileList[0].originFileObj;
    if (file) {
      importMutation.mutate(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const blob = await prescriptionService.downloadSamplePrescription();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mau_nhap_toa_thuoc.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Tải file mẫu thành công!');
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Không thể tải file mẫu!'));
    } finally {
      setDownloading(false);
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload: () => false,
    onChange: handleUploadChange,
    fileList,
    accept: '.xlsx,.xls',
    maxCount: 1,
  };

  return (
    <div className="import-prescription-page">
      <h1 className="page-title">Nhập toa thuốc excel</h1>

      <div className="import-section">
        <Row gutter={48} align="middle">
          <Col>
            <div className="upload-section">
              <Text className="section-label">Tải file exel lên</Text>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Tải file exel lên</Button>
              </Upload>
            </div>
          </Col>
          <Col>
            <div className="template-section">
              <Text className="section-label">File excel mẫu</Text>
              <div className="template-link">
                <FileExcelOutlined className="excel-icon" />
                <Link onClick={handleDownloadTemplate} disabled={downloading}>
                  {downloading ? 'Đang tải...' : 'Tải file excel mẫu nhập toa thuốc'}
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="form-actions">
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleImport}
          loading={importMutation.isPending}
          size="large"
        >
          Lưu toa thuốc
        </Button>
      </div>
    </div>
  );
};

export default ImportPrescriptionPage;
